import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageIcon, SearchIcon, ZapIcon } from 'lucide-react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIssueReports } from '@/hooks/useIssueReports';
import { IssueKind, IssueReportQuery, IssueReportSummary, IssueSeverity, IssueStatus } from '@/schema/issueReport';
import { Column, DataTable } from '../components/DataTable';
import { SectionCard } from '../components/SectionCard';
import { SEVERITY_STYLES, STATUS_LABELS, STATUS_STYLES } from '../components/issueStyles';
import { EM_DASH, formatRelative } from '../lib/format';

const PAGE_SIZE = 15;
const ANY = 'ANY';

export function AdminIssues() {
  useDocumentTitle('Admin - Issues');
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [searchDraft, setSearchDraft] = useState('');
  const [query, setQuery] = useState<IssueReportQuery>({});

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery((current) => ({ ...current, search: searchDraft || undefined }));
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  const { data, isLoading } = useIssueReports(page, PAGE_SIZE, query);

  const patchQuery = (patch: Partial<IssueReportQuery>) => {
    setQuery((current) => ({ ...current, ...patch }));
    setPage(0);
  };

  const columns: Column<IssueReportSummary>[] = [
    {
      key: 'title',
      header: 'Report',
      render: (row) => (
        <div className="flex min-w-0 items-center gap-2">
          {row.kind === IssueKind.autoError ? (
            <ZapIcon className="h-3.5 w-3.5 shrink-0 text-amber-300" aria-label="Captured automatically" />
          ) : null}
          {row.hasScreenshot ? (
            <ImageIcon className="h-3.5 w-3.5 shrink-0 text-white/35" aria-label="Has a screenshot" />
          ) : null}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{row.title}</p>
            <p className="truncate text-xs text-white/45">{row.route ?? EM_DASH}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (row) => <span className={SEVERITY_STYLES[row.severity]}>{row.severity}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <span className={STATUS_STYLES[row.status]}>{STATUS_LABELS[row.status]}</span>,
    },
    {
      key: 'reporter',
      header: 'Reported by',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-white/80">{row.reportedByEmail ?? EM_DASH}</p>
          <p className="truncate text-xs text-white/45">{row.clinicName ?? EM_DASH}</p>
        </div>
      ),
    },
    {
      key: 'created',
      header: 'When',
      render: (row) => <span className="text-sm text-white/70">{formatRelative(row.createdAt)}</span>,
    },
  ];

  return (
    <SectionCard
      title="Issue reports"
      description="Problems reported from inside the app, with the context needed to reproduce them."
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
          <Input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Search title, description or route"
            className="pl-9"
          />
        </div>

        <Select
          value={query.status ?? ANY}
          onValueChange={(value) =>
            patchQuery({ status: value === ANY ? undefined : (value as IssueStatus) })
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={ANY}>Any status</SelectItem>
              {Object.values(IssueStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        <Select
          value={query.severity ?? ANY}
          onValueChange={(value) =>
            patchQuery({ severity: value === ANY ? undefined : (value as IssueSeverity) })
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={ANY}>Any severity</SelectItem>
              {Object.values(IssueSeverity).map((severity) => (
                <SelectItem key={severity} value={severity}>
                  {severity}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.content ?? []}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/admin/issues/${row.id}`)}
        page={page}
        totalPages={data?.totalPages ?? 0}
        onPageChange={setPage}
        isLoading={isLoading}
        emptyMessage="No reports match these filters."
      />
    </SectionCard>
  );
}

export default AdminIssues;
